import { PAGESIZE_DEFAULT, SEARCH_KEYWORDS } from "./constants";
import { SearchRequest } from "./types";

export function parseSearchToParams(
	searchParams: URLSearchParams,
): SearchRequest {
	const params: SearchRequest = {};

	const query = searchParams.get(SEARCH_KEYWORDS.query) || undefined;

	if (query) {
		params.query = query;
	}

	const pageSize = parseInt(
		searchParams.get(SEARCH_KEYWORDS.pageSize) || String(PAGESIZE_DEFAULT),
	);
	params.perPage = pageSize;

	const page = Number(searchParams.get(SEARCH_KEYWORDS.page) || "1");
	params.page = page;

	const sortBy = searchParams.get(SEARCH_KEYWORDS.sortBy) || undefined;
	if (sortBy) {
		params.sortBy = sortBy;
	}

	const filters: SearchRequest["filters"] = {};

	searchParams.forEach((value, key) => {
		if (key.startsWith(SEARCH_KEYWORDS.filter)) {
			const field = key.replace(SEARCH_KEYWORDS.filter, "");

			const values = value.split(",").filter((value) => value.trim() !== "");

			filters[field] = values.map((value) => ({
				value,
				isBackgroundFilter: false,
			}));
		}
	});

	params.filters = filters;

	return params;
}

const isWordPressQuery = (query: string) => {
	const matches = [
		"wp-content",
		"wp-includes",
		"wp-config.php",
		"wp-settings.php",
	];

	return matches.some((match) => query.toLowerCase().includes(match));
};

const isFileType = (query: string) => {
	const matches = [
		".pdf",
		".doc",
		".docx",
		".xls",
		".xlsx",
		".ppt",
		".pptx",
		".txt",
		".jpg",
		".jpeg",
		".png",
		".gif",
		".zip",
		".rar",
		".html",
		".htm",
		".js",
		".ts",
		".css",
		".php",
		".asp",
		".aspx",
		".jsp",
	];

	return matches.some((match) => query.toLowerCase().endsWith(match));
};

const detectSQLInjection = (input: string) => {
	let score = 0;

	// 1. Check for mismatched or excessive brackets
	const opens = (input.match(/\(/g) || []).length;
	const closes = (input.match(/\)/g) || []).length;
	if (opens !== closes || opens > 3) score += 2;

	// 2. Check for SQL Hex/Char encoding (like chr() or 0x)
	if (/chr\(/i.test(input) || /0x[0-9a-f]+/i.test(input)) score += 5;

	// 3. Check for common SQL keywords and comment symbols
	const sqlPatterns =
		/\b(SELECT|UNION|INSERT|DELETE|DROP|UPDATE|SLEEP|OR|AND|--|#|\/\*)\b/gi;
	if (sqlPatterns.test(input)) score += 3;

	// 4. Check for pipe/concatenation symbols (like ||)
	if (input.includes("||") || input.includes("&&")) score += 2;

	return {
		isSuspicious: score >= 5, // Threshold
		score: score,
	};
};

const detectHTMLInjection = (input: string) => {
	let score = 0;

	// 1. Check for basic tag structure: <anything>
	// This catches <div>, <br>, etc.
	const tagPattern = /<[^>]+>/g;
	const tagsFound = (input.match(tagPattern) || []).length;
	score += tagsFound * 2;

	// 2. High-Alert Tags (Scripts, Iframes, Objects)
	const dangerousTags = /<(script|iframe|object|embed|applet|meta|link|style)/i;
	if (dangerousTags.test(input)) score += 10;

	// 3. Event Handlers (The "on" attributes like onload, onerror, onclick)
	// These allow XSS even without a <script> tag (e.g., <img src=x onerror=alert(1)>)
	const eventHandlers = /\bon[a-z]+\s*=/i;
	if (eventHandlers.test(input)) score += 8;

	// 4. "javascript:" pseudo-protocol
	if (/javascript:/i.test(input)) score += 5;

	return {
		isSuspicious: score >= 5,
		score: score,
		foundTags: tagsFound,
	};
};

export const isValidSearchQuery = (
	query: string,
	emptyQueryAllowed: boolean = false,
): boolean => {
	// if the query is empty but we're searching in a category
	// that's ok
	if (!query && emptyQueryAllowed) {
		return true;
	}

	const MIN_LENGTH = 3;
	const MAX_LENGTH = 120;

	const q = query.trim().toLowerCase();

	if (q.length < MIN_LENGTH) {
		return false;
	}

	// WordPress queries are not good
	if (isWordPressQuery(q)) {
		return false;
	}

	// file type queries are not good
	if (isFileType(q)) {
		return false;
	}

	let score = 0;

	// long queries are not good
	if (q.length > MAX_LENGTH) {
		score += 1;
	}

	const sqlTest = detectSQLInjection(q);
	const htmlTest = detectHTMLInjection(q);

	score += sqlTest.score;
	score += htmlTest.score;

	if (score >= 5) {
		console.error(
			"Suspicious search query blocked",
			q,
			"Overall Score:",
			score,
			"SQL Test:",
			sqlTest,
			"HTML Test:",
			htmlTest,
		);
	}

	return score < 5;
};
